import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import JsonLd from "@/components/base/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PostCard from "@/components/feature/PostCard";
import HotRanking from "@/components/feature/HotRanking";
import { categories, posts } from "@/mocks/posts";

const ORIGIN = "https://ymw88.top";

const catTabs = [{ name: "全部", slug: "all" }, ...categories.map((c) => ({ name: c.name, slug: c.slug }))];

export default function Breaking() {
  useSeo({
    title: "今日爆料列表 - 黑吃瓜网新瓜汇总与猛料检索",
    description:
      "黑吃瓜网今日爆料列表，支持关键词搜索与主题分类筛选，新瓜汇总、热门猛料按热度排序，快速定位你关心的娱乐话题。",
    keywords: "今日爆料列表,新瓜汇总,热门猛料搜索,黑吃瓜网",
    path: "/breaking",
  });

  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const cat = params.get("cat") ?? "all";
  const sort = params.get("sort") ?? "latest";
  const [input, setInput] = useState(q);

  useEffect(() => {
    setInput(q);
  }, [q]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all" && value !== "latest") {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setParams(next, { replace: true });
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    updateParam("q", input.trim());
  };

  const keyword = q.trim().toLowerCase();
  const filtered = posts
    .filter((p) => (cat === "all" ? true : p.categorySlug === cat))
    .filter((p) =>
      keyword
        ? p.title.toLowerCase().includes(keyword) ||
          p.excerpt.toLowerCase().includes(keyword) ||
          p.tags.some((t) => t.toLowerCase().includes(keyword))
        : true
    )
    .sort((a, b) => (sort === "hot" ? b.heat - a.heat : b.date.localeCompare(a.date)));

  const activeCat = catTabs.find((t) => t.slug === cat) ?? catTabs[0];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "今日爆料列表",
      url: `${ORIGIN}/breaking`,
      description: "黑吃瓜网今日爆料与新瓜汇总列表，支持分类筛选与关键词检索。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "今日爆料", item: `${ORIGIN}/breaking` },
      ],
    },
  ];

  return (
    <>
      <JsonLd data={jsonLd} />

      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "今日爆料" }]} />
          <h1 className="mt-4 font-heading font-bold text-3xl md:text-4xl text-foreground-950">
            今日爆料列表
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            这里汇总了黑吃瓜网所有已发布的话题与猛料。用关键词搜索，或者按主题分类浏览，找到你最想追的那一条。
          </p>

          <form onSubmit={handleSearch} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line" aria-hidden="true"></i>
              </span>
              <input
                type="search"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索话题关键词，如：定档、音乐节、联名"
                aria-label="搜索爆料关键词"
                className="w-full pl-11 pr-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-2">
              {catTabs.map((tab) => {
                const isActive = cat === tab.slug;
                return (
                  <button
                    key={tab.slug}
                    type="button"
                    onClick={() => updateParam("cat", tab.slug)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                      isActive
                        ? "bg-primary-500 text-background-50 border-primary-500"
                        : "bg-background-50 text-foreground-700 border-background-300 hover:border-primary-400 hover:text-primary-700"
                    }`}
                  >
                    {tab.name}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-foreground-500 whitespace-nowrap">排序</span>
              {[
                { key: "latest", label: "最新发布" },
                { key: "hot", label: "热度最高" },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => updateParam("sort", item.key)}
                  className={`px-3 py-1.5 rounded-md text-sm border transition-colors cursor-pointer whitespace-nowrap ${
                    sort === item.key
                      ? "bg-secondary-100 text-secondary-900 border-secondary-300"
                      : "bg-background-50 text-foreground-600 border-background-300 hover:border-secondary-400"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <p className="text-sm text-foreground-600 mb-6">
            当前筛选：<strong className="text-foreground-950">{activeCat.name}</strong>
            {keyword && <> · 关键词「<strong className="text-foreground-950">{q}</strong>」</>}
            {" "}共找到 <strong className="text-foreground-950">{filtered.length}</strong> 条内容
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
            <div className="lg:col-span-9">
              {filtered.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {filtered.map((post) => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>
              ) : (
                <div className="py-20 text-center border border-dashed border-background-300 rounded-lg">
                  <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
                  </span>
                  <h3 className="font-heading font-bold text-lg text-foreground-950">没有找到相关内容</h3>
                  <p className="mt-2 text-sm text-foreground-600">换个关键词，或者清空筛选看看全部爆料。</p>
                  <button
                    type="button"
                    onClick={() => setParams(new URLSearchParams(), { replace: true })}
                    className="mt-5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    清空筛选
                  </button>
                </div>
              )}
            </div>

            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-28 space-y-5">
                <HotRanking limit={5} />
                <div className="p-5 rounded-lg border border-background-200 bg-background-100">
                  <h3 className="font-heading font-bold text-base text-foreground-950">订阅更新提醒</h3>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">
                    不想错过突发猛料？在联系我们页面留下邮箱，我们会把每周精选发给你。
                  </p>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap"
                  >
                    前往订阅
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}